/**
 * The cashflow & pension Statement of Advice, in the shared document's shape
 * (features/advice/moduleSoa.ts). Quotes the option's own snapshot and projections and the
 * change-nothing option's.
 */
import { moduleSoaCommon, replacementsFor, piecesForKinds, whyItFitsFrom, projectionRowsFrom, COMMON_IN_SCOPE, COMMON_OUT_OF_SCOPE, type ModuleSoaSnapshot, type SoaMoneyRow, type SoaEstateDriver, type SoaProjectionRow } from '../advice/moduleSoa.ts';
import type { SoaComparisonRow } from '../advice/soaKit.ts';
import { riskKindsFromCandidateId, type RiskKind } from '../../strategy/strategyRisks.ts';
import type { DraftFactFind } from '../../constants/initialFactFind.ts';
import type { CashflowPensionResult, SupportCostInput } from './cashflowPensionScenario.ts';

export interface CpSoaParams {
    option: CashflowPensionResult;
    all: CashflowPensionResult[];
    /** Which option led on each goal, as the page works it out: goal label → option id. */
    leaders: { label: string; id: string }[];
    factFind: DraftFactFind;
    legacyYears: number;
    supportCost: SupportCostInput | null;
}

const n = (v: unknown): number => {
    const x = typeof v === 'number' ? v : parseFloat(String(v ?? ''));
    return Number.isFinite(x) ? x : 0;
};
const money = (v: number): string => `$${Math.round(v).toLocaleString('en-AU')}`;

export function buildCashflowPensionSoaSnapshot(p: CpSoaParams): ModuleSoaSnapshot {
    const { option, factFind } = p;
    const id = String(option.id || '');
    const kinds = riskKindsFromCandidateId(id);
    const years = Number(p.legacyYears) || 10;
    const nothing = p.all.find((s) => s.changeNothing) || null;
    const headline = { pension: n(option.snapshot?.pension), cashflow: n(option.snapshot?.cashflow), estate: n(option.snapshot?.estate) };
    const baseline = { name: 'Changing nothing', pension: n(nothing?.snapshot?.pension), cashflow: n(nothing?.snapshot?.cashflow), estate: n(nothing?.snapshot?.estate) };
    const terms = (option.annuityTerms || {}) as { purchaseAmount?: number };
    const annuityAmount = n(terms.purchaseAmount);

    const common = moduleSoaCommon(factFind, { kind: 'cashflow_pension', strategySource: 'suggestion_engine', riskKinds: kinds });

    // The option describes its own amounts in its name ("Gift $10,000 and buy a $50,000 annuity").
    const amountIn = (re: RegExp): number => {
        const m = re.exec(`${option.name} ${option.description}`);
        return m ? n(m[1]?.replace(/,/g, '')) : 0;
    };
    const amounts: Partial<Record<RiskKind, number>> = {
        annuityNil: annuityAmount, annuityReturned: annuityAmount, annuityGuaranteed: annuityAmount,
        funeralBond: amountIn(/\$([\d,]+)[^.]*funeral/i),
        gift: amountIn(/gift[^$]*\$([\d,]+)/i),
        superContribution: amountIn(/\$([\d,]+)[^.]*super/i),
        investmentBond: amountIn(/\$([\d,]+)[^.]*(investment )?bond/i),
    };
    const moneyFlow: SoaMoneyRow[] = [
        ...(annuityAmount > 0 ? [{ label: 'Lifetime annuity bought', amount: annuityAmount }] : []),
        ...(amounts.funeralBond ? [{ label: 'Funeral bond', amount: amounts.funeralBond }] : []),
        ...(amounts.gift ? [{ label: 'Gift to family', amount: amounts.gift }] : []),
        ...(amounts.superContribution ? [{ label: 'Into super', amount: amounts.superContribution }] : []),
        ...(amounts.investmentBond ? [{ label: 'Investment bond', amount: amounts.investmentBond }] : []),
    ];
    const pieces = piecesForKinds(kinds, amounts);
    const inBrief = [option.description || option.name, ...whyItFitsFrom(headline, baseline, years)].filter(Boolean);

    const alternatives = p.all
        .filter((s) => !s.error && (p.leaders.some((l) => l.id === s.id) || s.id === option.id))
        .map((s) => ({
            name: s.name,
            description: s.description,
            pension: n(s.snapshot?.pension),
            cashflow: n(s.snapshot?.cashflow),
            estate: n(s.snapshot?.estate),
            selected: s.id === option.id,
            badges: p.leaders.filter((l) => l.id === s.id).map((l) => l.label),
        }));

    const support = p.supportCost;
    const allEvaluated: SoaComparisonRow[] = p.all
        .filter((s) => !s.error && !s.changeNothing)
        .map((s) => ({ name: s.name, description: s.description, pension: n(s.snapshot?.pension), cashflow: n(s.snapshot?.cashflow), estate: n(s.snapshot?.estate), selected: s.id === option.id, badges: p.leaders.filter((l) => l.id === s.id).map((l) => l.label) }));
    const projRows = (list: readonly unknown[] | undefined): SoaProjectionRow[] =>
        projectionRowsFrom(list, years, support ? { moduleCostLabel: support.label, moduleCost: () => support.annual } : {});
    const pensionDelta = Math.round(headline.pension - baseline.pension);
    const estateDrivers: SoaEstateDriver[] = [
        ...(amounts.gift ? [{ text: 'The gift leaves the estate on the day; it is in the family\'s hands now rather than later', amount: amounts.gift, sign: 'minus' as const }] : []),
        ...(kinds.includes('annuityNil') && annuityAmount > 0 ? [{ text: 'A nil-residual annuity leaves nothing of its price to the estate; its value is the income for life', amount: annuityAmount, sign: 'minus' as const }] : []),
        ...(kinds.includes('annuityReturned') && annuityAmount > 0 ? [{ text: 'The annuity\'s withdrawal value winds down on a set schedule; what is left at the horizon stays in the estate', amount: annuityAmount, sign: 'neutral' as const }] : []),
        ...(amounts.funeralBond ? [{ text: 'The funeral bond is set aside for the funeral, so it is not counted and not part of what is left to spend', amount: amounts.funeralBond, sign: 'neutral' as const }] : []),
        ...(pensionDelta !== 0 ? [{ text: `${pensionDelta > 0 ? 'More' : 'Less'} Age Pension, compounding over the ${years} years`, amount: Math.abs(pensionDelta), perYear: true, sign: (pensionDelta > 0 ? 'plus' : 'minus') as 'plus' | 'minus' }] : []),
        ...(headline.cashflow !== baseline.cashflow ? [{ text: `${headline.cashflow > baseline.cashflow ? 'Less' : 'More'} drawn from savings each year to cover the bills`, amount: Math.abs(Math.round(headline.cashflow - baseline.cashflow)), perYear: true, sign: (headline.cashflow > baseline.cashflow ? 'plus' : 'minus') as 'plus' | 'minus' }] : []),
    ];
    const motiveNote = headline.estate < baseline.estate - 1
        ? `An estate that ends behind ${baseline.name.toLowerCase()} is the price of the income and the pension this plan buys${support ? `, with ${money(support.annual)} a year of ${support.label.toLowerCase()} to pay for` : ''}. The trade is money you can rely on now against money left later; the figures show the size of it, so the decision is made with eyes open.`
        : null;
    return {
        ...common,
        decisionIntro: `You asked how to make your money go further at home${support ? `, with ${money(support.annual)} a year of ${support.label.toLowerCase()} to pay for` : ''}: lifting the Age Pension, covering the bills each year, and keeping as much as possible for the family. This advice is about the levers that do that, and what each one costs.`,
        selectedStrategyName: option.name,
        selectedStrategyDescription: option.description,
        inOneLine: option.description || option.name,
        inBrief,
        pieces,
        moneyFlow,
        legacyYears: years,
        headline,
        baseline,
        whyItFits: whyItFitsFrom(headline, baseline, years),
        estateDrivers,
        motiveNote,
        modulePage: support ? {
            title: 'Your spending',
            intro: 'The plan has to cover these each year. Where it does not, the projections draw the shortfall from savings.',
            rows: [
                { label: 'Living expenses', amount: common.annualLivingExpenses, note: 'a year, as you entered them' },
                { label: support.label, amount: support.annual, note: 'a year' },
            ],
        } : null,
        moduleCostLabel: support ? support.label : '',
        projectionConvention: 'Year 1 is the first full year after the change; each later year follows it.',
        alternatives,
        allEvaluated,
        totalEvaluated: allEvaluated.length,
        projections: projRows(option.projections),
        baselineProjections: projRows(nothing?.projections),
        replacements: replacementsFor(kinds, moneyFlow),
        implications: [
            ...(annuityAmount > 0 ? [{ title: 'The annuity needs a provider quote', lead: `The ${money(annuityAmount)} annuity is priced on a published rate sheet.`, body: 'The income shown is an estimate. Get a quote from the insurer before buying; if it differs, re-run the analysis on the real figure.' }] : []),
            ...(amounts.gift ? [{ title: 'Gifts have a limit', lead: 'Up to $10,000 a year and $30,000 over five years leaves the count.', body: 'Tell Services Australia about the gift. Anything above the limit still counts as your asset, and is deemed, for five years.' }] : []),
            { title: 'Tell Services Australia', lead: 'Each of these moves changes what is counted.', body: 'Report the change within 14 days so the pension is reassessed on the new figures.' },
        ],
        scope: {
            inScope: ['The levers that lift the Age Pension or free up cashflow while you stay at home, using the figures you entered.', ...COMMON_IN_SCOPE],
            outOfScope: ['Moving house, a village, aged care or a granny flat — each has its own module and its own advice.', ...COMMON_OUT_OF_SCOPE],
        },
        nextSteps: [
            ...(annuityAmount > 0 ? ['Get a quote from an annuity provider and re-run the analysis on it.'] : []),
            ...(amounts.funeralBond ? ['Compare funeral bond providers on fees; the exemption limit is the same whichever you choose.'] : []),
            ...(amounts.superContribution ? ['Check with your super fund that the contribution can be accepted at your age and balance.'] : []),
            'Tell Services Australia about each change within 14 days.',
            'Where you sell assets or trigger capital gains tax, confirm the tax with a registered tax agent before you act.',
        ],
    };
}
